<template>
  <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
    <!-- Stats Cards -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-gray-600 text-sm">Total Operations</p>
          <p class="text-3xl font-bold text-gray-800">{{ stats.total || 0 }}</p>
        </div>
        <div class="text-4xl">🎯</div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-gray-600 text-sm">Caught</p>
          <p class="text-3xl font-bold text-green-600">{{ stats.caught || 0 }}</p>
        </div>
        <div class="text-4xl">✅</div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-gray-600 text-sm">Released</p>
          <p class="text-3xl font-bold text-blue-600">{{ stats.released || 0 }}</p>
        </div>
        <div class="text-4xl">🚀</div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-gray-600 text-sm">Pending</p>
          <p class="text-3xl font-bold text-yellow-600">{{ stats.pending || 0 }}</p>
        </div>
        <div class="text-4xl">⏳</div>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Recent Operations -->
    <div class="lg:col-span-2 bg-white rounded-lg shadow">
      <div class="px-6 py-4 border-b">
        <h3 class="text-lg font-semibold">Recent Operations</h3>
      </div>
      <div v-if="loading" class="p-6 text-center">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="catchings.length === 0" class="p-6 text-center text-gray-500">
        No operations yet. <router-link to="/operations/catchings/new" class="text-blue-600">Create one</router-link>
      </div>
      <div v-else class="divide-y">
        <div v-for="catching in catchings.slice(0, 5)" :key="catching.id" class="px-6 py-4 hover:bg-gray-50">
          <div class="flex justify-between items-start">
            <div>
              <p class="font-semibold">{{ catching.animal?.name }} - {{ catching.ward }}</p>
              <p class="text-sm text-gray-600">{{ catching.animal_reg_id }}</p>
              <p class="text-xs text-gray-500">{{ new Date(catching.catching_date).toLocaleDateString() }}</p>
            </div>
            <span :class="[
              'px-3 py-1 rounded-full text-xs font-semibold capitalize',
              catching.status === 'caught' ? 'bg-green-100 text-green-800' :
              catching.status === 'released' ? 'bg-blue-100 text-blue-800' :
              catching.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
            ]">
              {{ catching.status }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="space-y-4">
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-4">Quick Actions</h3>
        <div class="space-y-2">
          <router-link
            to="/operations/catchings/new"
            class="block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-center"
          >
            ➕ New Operation
          </router-link>
          <router-link
            to="/operations/catchings"
            class="block px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition text-center"
          >
            📋 View All
          </router-link>
          <router-link
            to="/masters/vehicles"
            class="block px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition text-center"
          >
            🚗 Vehicles
          </router-link>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-4">Your Info</h3>
        <div class="space-y-2 text-sm">
          <p><strong>Name:</strong> {{ authStore.user?.full_name }}</p>
          <p><strong>Role:</strong> <span class="capitalize">{{ authStore.user?.role }}</span></p>
          <p v-if="authStore.user?.district_id"><strong>District:</strong> {{ authStore.user?.district_id }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useCatchingStore } from '@/stores/catchingStore'

const authStore = useAuthStore()
const catchingStore = useCatchingStore()

const stats = ref({ total: 0, caught: 0, released: 0, pending: 0 })
const loading = ref(false)

const catchings = ref([])

onMounted(async () => {
  loading.value = true
  try {
    stats.value = await catchingStore.getStatistics() || {}
    catchings.value = await catchingStore.getCatchings(1) || []
  } finally {
    loading.value = false
  }
})
</script>
